import type { Cesium3DTileset, Viewer } from 'cesium';
import { BoxSelect, LocateFixed, Maximize, Move, Rotate3D, RotateCcw, X } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { boxBoundingSphere, type ClipBox, type LayerTileset } from '@/helper/clipping-box-geometry';
import { isSavedBoxLookedInto, placeBox } from '@/helper/clipping-box-placement';
import { useClippingBox } from '@/hooks/use-clipping-box';
import { useClippingSettings } from '@/hooks/use-clipping-settings';
import { useCesium } from '../../context/CesiumContext';

// The longest a new box waits for the model's tiles in view before it is placed anyway.
const TILE_WAIT_MS = 2000;

const flyToBox = (viewer: Viewer, box: ClipBox) => {
	viewer.camera.flyToBoundingSphere(boxBoundingSphere(box), { duration: 1.2 });
};

// Helper hook for Local Storage syncing
function useLocalStorageToggle(key: string, initialValue: boolean): [boolean, (val: boolean) => void] {
	const [value, setValue] = useState<boolean>(() => {
		try {
			const item = window.localStorage.getItem(key);
			return item ? JSON.parse(item) : initialValue;
		} catch (error) {
			console.error('Error reading localStorage', error);
			return initialValue;
		}
	});

	const setItem = (newValue: boolean) => {
		setValue(newValue);
		try {
			window.localStorage.setItem(key, JSON.stringify(newValue));
		} catch (error) {
			console.error('Error setting localStorage', error);
		}
	};

	return [value, setItem];
}

type Props = {
	tilesets: Cesium3DTileset[];
	enabled: boolean;
	onClose: () => void;
};

const ClippingBoxManager: React.FC<Props> = ({ tilesets, enabled, onClose }) => {
	const { t } = useTranslation();
	const { viewer } = useCesium();
	const { assetId } = useParams<{ assetId: string }>();
	const { getSettings, updateSettings } = useClippingSettings(assetId);

	// Read from storage each time the tool opens and dropped when it closes.
	const [box, setBox] = useState<ClipBox | null>(null);
	// The box on screen, drags included. The stored box can be older: a box placed on open is
	// stored only once the user changes it.
	const shownBox = useRef<ClipBox | null>(null);
	// The box a running reset replaces, shown again if the reset finds nothing.
	const replacedBox = useRef<ClipBox | null>(null);
	const showBox = useCallback((next: ClipBox | null) => {
		shownBox.current = next;
		setBox(next);
	}, []);
	// Waits for the next frame, once any old box is gone from the depth buffer: on open it decides
	// about the saved box, on reset it places a new one.
	const [pending, setPending] = useState<'open' | 'reset' | null>(null);
	// The last placement found nothing under the centre of the view.
	const [nothingToPlaceOn, setNothingToPlaceOn] = useState(false);

	// Default configuration: Only Scale is active, Rotate and Move are disabled by default
	const [enableScale, setEnableScale] = useLocalStorageToggle('clipping-box-scale', true);
	const [enableRotate, setEnableRotate] = useLocalStorageToggle('clipping-box-rotate', true);
	const [enableMove, setEnableMove] = useLocalStorageToggle('clipping-box-move', true);

	// Cesium does not apply clipping planes to Gaussian splats; every other layer is clipped.
	const clipTilesets = useMemo(() => tilesets.filter((tileset) => !(tileset as LayerTileset).isSplat), [tilesets]);
	const hasSplat = clipTilesets.length < tilesets.length;

	const handleSaveState = useCallback(
		(next: ClipBox) => {
			shownBox.current = next;
			updateSettings({ box: next });
		},
		[updateSettings],
	);

	useClippingBox(viewer, clipTilesets, enabled, {
		box,
		enableScale,
		enableRotate,
		enableMove,
		onSaveState: handleSaveState,
	});

	// The placement effect must not re-run when a layer loads, so it reads the layers through a ref.
	const latest = useRef({ tilesets });
	latest.current = { tilesets };

	useEffect(() => {
		if (!enabled) {
			showBox(null);
			setPending(null);
			setNothingToPlaceOn(false);
			return;
		}
		setPending('open');
	}, [enabled, showBox]);

	useEffect(() => {
		if (!enabled || !pending || !viewer) return;

		const { scene } = viewer;
		const startedAt = Date.now();
		// Registered after dropComputeCommandsAfterRender (viewer creation), so a translucent
		// depth pick here does not run that frame's compute commands again.
		const removeListener = scene.postRender.addEventListener(() => {
			// Tiles still loading in view would let the pick hit the terrain behind the model.
			const loading = latest.current.tilesets.some((tileset) => tileset.show && !tileset.tilesLoaded);
			if (loading && Date.now() - startedAt < TILE_WAIT_MS) {
				scene.requestRender();
				return;
			}

			removeListener();
			// This runs inside Cesium's render loop, where an exception stops all rendering.
			try {
				const saved = getSettings().box;
				// A saved box comes back only when the user looks into it; one beside the model would
				// clip away what they look at.
				if (pending === 'open' && saved && isSavedBoxLookedInto(viewer, saved)) {
					showBox(saved);
					return;
				}
				const next = placeBox(viewer, latest.current.tilesets);
				setNothingToPlaceOn(!next);
				if (next) {
					showBox(next);
					// A box placed on open is stored once the user changes it, so a saved box the user
					// does not look at now stays for later; a reset is the user's choice.
					if (pending === 'reset') updateSettings({ box: next });
				} else {
					// A reset that finds nothing brings the old box back; an open shows none.
					showBox(pending === 'reset' ? replacedBox.current : null);
				}
			} catch (error) {
				console.error('Failed to place the clipping box', error);
				showBox(pending === 'reset' ? replacedBox.current : null);
			} finally {
				setPending(null);
			}
		});
		scene.requestRender();
		return removeListener;
	}, [enabled, pending, viewer, getSettings, updateSettings, showBox]);

	if (!enabled || tilesets.length === 0) return null;

	const handleReset = () => {
		setNothingToPlaceOn(false);
		// The placement waits for a frame without the old box and its clipping.
		replacedBox.current = shownBox.current;
		showBox(null);
		setPending('reset');
	};

	const handleGoToBox = () => {
		const current = shownBox.current;
		if (viewer && current) flyToBox(viewer, current);
	};

	// Helper sub-component for repeated toggle switch UI
	const ToggleRow = ({ label, icon: Icon, checked, onChange }: any) => (
		<div className="flex items-center justify-between">
			<span className="text-xs text-muted-foreground flex items-center gap-1.5">
				<Icon className="w-3.5 h-3.5" /> {label}
			</span>
			<label className="flex items-center justify-between cursor-pointer group select-none">
				<input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
				<div
					className={`w-9 h-5 rounded-full transition-colors duration-200 ease-in-out ${
						checked ? 'bg-primary' : 'bg-foreground/20'
					}`}
				>
					<div
						className={`h-4 w-4 bg-background rounded-full shadow transform transition-transform mt-0.5 ml-0.5 ${
							checked ? 'translate-x-4' : ''
						}`}
					/>
				</div>
			</label>
		</div>
	);

	return (
		<div className="absolute top-20 right-4 w-72 z-20 pointer-events-none">
			<div className="pointer-events-auto bg-card/95 backdrop-blur shadow-lg rounded-lg border border-border overflow-hidden">
				{/* Header */}
				<div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-foreground/20">
					<div className="flex items-center gap-2">
						<BoxSelect className="w-4 h-4 text-primary" />
						<span className="text-xs font-semibold text-card-foreground">{t('viewer.clipping.box.title')}</span>
					</div>
					<button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
						<X className="w-4 h-4" />
					</button>
				</div>

				<div className="p-3 space-y-4">
					{hasSplat && <p className="text-[10px] text-muted-foreground">{t('viewer.clipping.box.splatNotClipped')}</p>}
					{nothingToPlaceOn && (
						<p className="text-[10px] text-muted-foreground">{t('viewer.clipping.box.nothingToPlaceOn')}</p>
					)}

					{(enableScale || enableMove || enableRotate) && (
						<div className="text-[10px] text-muted-foreground bg-muted/30 p-2 rounded border border-foreground/20 leading-relaxed space-y-1">
							<strong className="text-foreground/60">{t('viewer.clipping.box.interactions')}</strong>
							<ul className="list-disc pl-3 mt-1 space-y-1">
								{enableScale && (
									<li>
										{t('viewer.clipping.box.dragScaleBefore')}
										<strong className="text-foreground/60">{t('viewer.clipping.box.dragScaleTerm')}</strong>
										{t('viewer.clipping.box.dragScaleAfter')}
									</li>
								)}
								{enableMove && (
									<li>
										{t('viewer.clipping.box.dragMoveBefore')}
										<strong className="text-foreground/60">{t('viewer.clipping.box.dragMoveTerm')}</strong>
										{t('viewer.clipping.box.dragMoveAfter')}
									</li>
								)}
								{enableRotate && (
									<li>
										{t('viewer.clipping.box.dragRotateBefore')}
										<strong className="text-foreground/60">{t('viewer.clipping.box.dragRotateTerm')}</strong>
										{t('viewer.clipping.box.dragRotateAfter')}
									</li>
								)}
							</ul>
						</div>
					)}

					{/* Tool Option Toggles */}
					<div className="space-y-3">
						<ToggleRow
							label={t('viewer.clipping.box.enableScaling')}
							icon={Maximize}
							checked={enableScale}
							onChange={setEnableScale}
						/>
						<ToggleRow
							label={t('viewer.clipping.box.enableRotation')}
							icon={Rotate3D}
							checked={enableRotate}
							onChange={setEnableRotate}
						/>
						<ToggleRow
							label={t('viewer.clipping.box.enableMovement')}
							icon={Move}
							checked={enableMove}
							onChange={setEnableMove}
						/>
					</div>

					{/* Box Actions */}
					<div className="mt-2 grid grid-cols-2 gap-2">
						<button
							type="button"
							onClick={handleReset}
							disabled={pending !== null}
							title={t('viewer.clipping.box.resetBoxHint')}
							className="flex items-center justify-center gap-1.5 py-1.5 px-2 border border-foreground/20 rounded-md text-xs text-muted-foreground hover:bg-muted/50 hover:text-primary transition-colors disabled:opacity-50 disabled:pointer-events-none"
						>
							<RotateCcw className="w-3 h-3 shrink-0" />
							{t('viewer.clipping.box.resetBox')}
						</button>
						<button
							type="button"
							onClick={handleGoToBox}
							disabled={!box}
							className="flex items-center justify-center gap-1.5 py-1.5 px-2 border border-foreground/20 rounded-md text-xs text-muted-foreground hover:bg-muted/50 hover:text-primary transition-colors disabled:opacity-50 disabled:pointer-events-none"
						>
							<LocateFixed className="w-3 h-3 shrink-0" />
							{t('viewer.clipping.box.goToBox')}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ClippingBoxManager;
